import React, { useEffect, useMemo, useRef, useState } from 'react';

// Components
import CustomButton from '../../components/CustomButton';
import Label from '../../components/Label';
import FormikTextField from '../../components/form/FormikTextField';

// Constants
import { BUTTON_TYPES, LABEL_TYPES, TINY_MCE_PLUGINS } from '../../constants/common';

// Prime-React Components
import { Dialog } from 'primereact/dialog';

//redux
import { useDispatch, useSelector } from 'react-redux';
import {
  addEmailTemplate,
  editEmailTemplate,
  getEmailTemplateById
} from '../../redux/actions/emailTemplates';
import { apiStatusClear } from '../../redux/slices/apiStatus';
import { updateData } from '../../redux/slices/emailTemplates';

// Utility Package
import { Editor } from '@tinymce/tinymce-react';
import { useFormik } from 'formik';
import { getFormErrorMessage, isDarkMode } from '../../utils/common';
import { addEditEmailTemplateSchema } from '../../schema/masterData.Schema';

const AddEditEmailTemplates = (props) => {
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const { show, ID, onHide } = props;
  const { contentDetails, fieldDetails } = useSelector((state) => state.emailTemplate);
  const { isSucceed, isLoading } = useSelector((state) => state.apiStatus);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [emailTemplateData, setEmailTemplateData] = useState({
    name: '',
    subject: '',
    description: ''
  });

  useEffect(() => {
    ID && dispatch(getEmailTemplateById(ID));
  }, [dispatch, ID]);

  const templateText = useMemo(() => {
    return `<ul style="list-style-type: decimal; padding-left: 15px;">${fieldDetails
      ?.map(({ key, comment }) => `<li><b>${key}</b> : ${comment}</li>`)
      .join('')}</ul>`;
  }, [fieldDetails]);

  useEffect(() => {
    if (ID && contentDetails && Object.keys(contentDetails).length !== 0) {
      setEmailTemplateData({
        name: `${contentDetails?.Title || ''}`,
        subject: `${contentDetails?.Subject || ''}`,
        description: `${contentDetails?.Content || ''}`
      });
    }
  }, [contentDetails]);

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide(false);
      formik.resetForm();
      dispatch(updateData());
      dispatch(apiStatusClear());
      setEmailTemplateData({
        name: '',
        description: ''
      });
      setIsUpdatedSuccess(false);
    }
  }, [isSucceed]);

  useEffect(() => () => setIsUpdatedSuccess(false), []);

  const formik = useFormik({
    initialValues: emailTemplateData,
    validationSchema: addEditEmailTemplateSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    if (ID) {
      dispatch(
        editEmailTemplate({
          formdata: {
            title: values.name,
            subject: values.subject,
            content: values.description
          },
          ID: ID
        })
      );
    } else {
      dispatch(
        addEmailTemplate({
          title: values.name,
          subject: values.subject,
          content: values.description
        })
      );
    }
    setIsUpdatedSuccess(true);
  };

  return (
    <Dialog
      header={`${ID ? 'Edit/View' : 'Add'} Email Template`}
      visible={show}
      style={{ width: '850px' }}
      onHide={() => {
        onHide(false);
      }}>
      <div className="form-box-wrapper">
        <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off">
          <div className="form-row-wrapper">
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.EMAIL_TEMPLATE_TITLE}
                    isMandatory
                    formik={formik}
                    name="name"
                    placeholder={LABEL_TYPES.EMAIL_TEMPLATE_TITLE}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.EMAIL_TEMPLATE_SUBJECT}
                    isMandatory
                    formik={formik}
                    name="subject"
                    placeholder={LABEL_TYPES.EMAIL_TEMPLATE_SUBJECT}
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label for="subject" text={LABEL_TYPES.EMAIL_TEMPLATE_PARAMETERS} />
                  <Editor
                    value={templateText}
                    init={{
                      height: 150,
                      menubar: false,
                      toolbar: false,
                      branding: false,
                      statusbar: false,
                      plugins: [],
                      readonly: true,
                      ...(isDarkMode() && { content_style: `body { color: #FFF }` })
                    }}
                  />
                  <p className="font-semibold text-sm mt-1">
                    Note: Use these parameters only for dynamic placeholders. They will be
                    auto-replaced in the final email and avoid using them as plain text.
                  </p>
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label for="description" text={LABEL_TYPES.EMAIL_TEMPLATE_CONTENT} isMandatory />
                  <Editor
                    id="description"
                    name="description"
                    onInit={(_evt, editor) => (editorRef.current = editor)}
                    value={formik.values.description}
                    init={{
                      height: 500,
                      menubar: true,
                      branding: false,
                      plugins: TINY_MCE_PLUGINS,
                      ...(isDarkMode() && { content_style: `body { color: #FFF }` })
                    }}
                    onEditorChange={(editor) => formik.setFieldValue('description', editor)}
                  />
                </div>
                {getFormErrorMessage(formik, 'description')}
              </div>
            </div>
          </div>
          <div className="form-btn-wrapper justify-flex-end">
            <CustomButton
              variant="contained"
              onClick={() => {
                onHide(false);
                setEmailTemplateData({
                  name: '',
                  subject: '',
                  description: ''
                });
                formik.resetForm();
              }}
              color="error"
              className="gray-btn">
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
            <CustomButton type="submit" variant="contained" className="" disabled={isLoading}>
              {BUTTON_TYPES.SAVE}
            </CustomButton>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AddEditEmailTemplates;
